import { renderVariables } from '../protocol/variables.js';
import { NOYAUX } from '../schemas/noyau.js';
import type { ProtocolStepDraft } from '../schemas/protocol.js';

/**
 * THE INSTRUMENT of the analyst (noyau.md §4.3, §4.9; issue #154) - the system
 * block of its prompt, assembled from FOUR parts, in this order:
 *
 * | Part | Written by |
 * |---|---|
 * | the framing (« Tu es l'analyste d'un dispositif d'entretien... ») | the code |
 * | the grid of Patrick Mathieu (§2), condensed | the code, the same for every protocol |
 * | « Comment lire la matière »: the heading, then the READING | the heading in code, the reading by the PROTOCOL played (N32) |
 * | the output contract (§4.2, six notes in JSON) | the code: it is what the program reads |
 *
 * ## Only the reading is a setting (P3)
 *
 * The grid is the method, which does not change from one protocol to the next,
 * and the contract is what `noyauAnalystOutputSchema` reads: a contract
 * retouched by hand would make every determination fail. The matter, though,
 * has the shape its protocol gives it - the three objectives of the Émergence
 * of PCQ are the three levels of the grid, those of « Scénario de succès » the
 * six boxes of a frame - so the protocol says how to read ITS matter, in the
 * field `analystReading` of its content, versioned and frozen with it (§4.9).
 * N15, « the whole prompt in code, a merge request per retouch », is
 * overturned: the trace of a reading is the protocol version that carries it.
 *
 * ## Here, in `shared`, and not in the backend (N41)
 *
 * The protocol editor shows « Ce que l'analyste recevra » while the reading is
 * typed, and it is rendered by the very functions the server assembles the
 * served system with: the panel and « l'entrée servie » cannot part company.
 * `analystSystemParts` is what the panel draws, part by part, and
 * `analystSystemPrompt` is those parts joined - the one spelling of both.
 *
 * ## The generic reading (P11, N34, N38)
 *
 * `ANALYST_READING_TEMPLATE` is the reading the code served to every protocol
 * until #154, kept as a TEMPLATE: a protocol created from now on is born with
 * it in its content, the protocols of before stay empty, and it is never served
 * on its own - except to a session started before the delivery, whose frozen
 * protocol has no reading (`servedAnalystReading`). Served, it rebuilds the
 * system of `ANALYST_PROMPT_VERSION` 1 to the byte, which is what makes those
 * sessions receive what they would have received.
 *
 * ## An objective cited by `@` renders its NAME (issue #158, N33 overturned)
 *
 * The reading cites an objective with the token of the directions,
 * `{{objectif:id}}`, and `resolveAnalystReading` turns it into the name the
 * protocol gives that objective - never its matter, which is what keeps the
 * reading, hence the system, identical from one session to the next (the
 * fingerprint and the cache, N39, N43). It runs BEFORE the assembly, on the
 * frozen protocol for the server and on the draft for the panel (N50), so
 * `analystSystemParts` keeps receiving a finished reading and stays the one
 * assembly of both. A token whose objective the protocol no longer carries
 * reads `ANALYST_READING_ORPHAN_LABEL` (V3); a renamed objective changes the
 * served system, hence the fingerprint (V4).
 *
 * ## None of the material of the client documents
 *
 * The grid is DESCRIBED from the base of Patrick Mathieu (`pms-digital/patrick`,
 * `.works/noyau/`): the three energies, the three levels with the three
 * questions that make each one said, how one energy changes face from one level
 * to the next, and the six triplets with the formula of each. Nothing of the
 * base that comes from its client documents: a prompt is sent to a provider,
 * and those documents are not to be spread.
 */

/** The heading of the third part - in code: the protocol writes the body of the section, not its title (N32). */
export const ANALYST_READING_TITLE = '## Comment lire la matière';

/**
 * The reading the code served to every protocol until #154 (§4.9, P11): the
 * template a created protocol is born with (N34), and the reading served to a
 * session started before the delivery whose frozen protocol carries none (N38).
 *
 * Three instructions, none of them by level: each noyau is noted on the matter
 * as a whole, a theme the interview imposed is not a preference, and a thin
 * matter gives moderate notes.
 */
export const ANALYST_READING_TEMPLATE = `La matière est faite d'extraits : les mots mêmes de la personne, rangés sous l'objectif de l'entretien auquel ils répondent. Le nom d'un objectif dit de quoi parlait l'entretien à ce moment-là ; il ne dit rien du noyau de la personne.

- Note chaque noyau sur l'ensemble de la matière : à quel point ce que dit la personne ressemble à ce noyau, tel que la grille le décrit.
- Les questions de l'entretien ont orienté ce que la personne a dit : un thème que l'entretien a imposé n'est pas une préférence de la personne. Juge ce qu'elle en fait, pas le sujet dont on lui a parlé.
- Tout profil lu par une grille est une hypothèse de lecture. Quand la matière est mince ou ambiguë, garde des notes modérées et proches les unes des autres plutôt que d'inventer un contraste.`;

const FRAMING = `Tu es l'analyste d'un dispositif d'entretien qui suit la méthode de Patrick Mathieu, la singularité trifonctionnelle. Tu ne parles jamais à la personne : tu lis la matière qu'elle a donnée pendant un entretien, et tu notes chacun des six noyaux de singularité. Ta réponse est lue par un programme.`;

const GRID_TITLE = '## La grille';

const GRID = `Trois énergies :
- S, Souverain : la puissance et la référence. Les valeurs, le savoir, la transmission, le sens ; la maîtrise de l'espace et du temps, l'ordre, la juste place, la vue d'ensemble, ce qui dure.
- G, Guerrier : l'engagement et l'action. L'énergie, la performance, le défi, l'audace ; la rupture, la transformation, le dépassement, le progrès, le futur.
- P, Producteur : la vie vécue. Les relations, les émotions, l'expérience, le beau ; les sensations, le désir, le plaisir, le soin, l'utilité, le présent et le quotidien.

Trois niveaux, toujours dans cet ordre :
- Pourquoi, la finalité : ce qui compte le plus pour la personne, ce qui la guide, sa vision à long terme. La question qui le fait dire : « Qu'est-ce qui est le plus important pour toi dans la vie ? »
- Comment, la modalité : sa manière de faire, sa méthode, sa façon d'être avec les autres. La question : « Comment fais-tu pour être sûr d'y parvenir ? »
- Quoi, la matérialité : ce que cela apporte concrètement, le bénéfice, le résultat. La question : « Qu'est-ce que cela apporte de façon concrète ? »

Chaque niveau est porté par une énergie, une de chaque : il y a donc six noyaux, chacun noté par son triplet dans l'ordre Pourquoi, Comment, Quoi.

La même énergie change de visage selon le niveau qu'elle occupe :
- Souverain : au Pourquoi, il règne (une vision, un ordre, des valeurs à transmettre) ; au Comment, il maîtrise (comprendre, organiser, faire référence) ; au Quoi, il donne une place (un statut, une légitimité, l'accès à l'essentiel).
- Guerrier : au Pourquoi, une raison de se battre (un destin, un combat, un dépassement) ; au Comment, une manière de conquérir (agir, oser, rompre, performer) ; au Quoi, une capacité d'agir (un progrès, un obstacle levé, une vie simplifiée).
- Producteur : au Pourquoi, la vie comme cause (les gens, le vécu, le lien) ; au Comment, la relation comme méthode (le soin, l'écoute, le collectif) ; au Quoi, le quotidien comme preuve (un bienfait concret, vécu, ici et maintenant).

Les six noyaux :
- SGP, Souverain Magicien : Pourquoi Souverain, Comment Guerrier, Quoi Producteur. Les maîtres de l'espace : que l'ordre règne et que chacun ait sa juste place, en élargissant activement leur univers pour y inclure tous ceux qu'ils peuvent, jusqu'à des bienfaits concrets.
- SPG, Souverain Juriste : Pourquoi Souverain, Comment Producteur, Quoi Guerrier. Les maîtres du temps : organiser la durée et protéger ce qui est essentiel à la vie, par le soin et la relation, jusqu'à un progrès qui permet d'agir.
- GSP, Guerrier Cosmique : Pourquoi Guerrier, Comment Souverain, Quoi Producteur. Prendre son destin en main et changer les règles, avec une vision et une maîtrise d'ensemble, jusqu'à un bien commun vécu au quotidien.
- GPS, Guerrier Humain : Pourquoi Guerrier, Comment Producteur, Quoi Souverain. Le dépassement et les défis matériels, relevés au contact du réel et avec les autres, jusqu'à une élévation et une reconnaissance.
- PGS, Producteur Séducteur : Pourquoi Producteur, Comment Guerrier, Quoi Souverain. Révéler la valeur de l'autre, avec intensité et réactivité, jusqu'à lui donner sa place.
- PSG, Producteur Réinventeur : Pourquoi Producteur, Comment Souverain, Quoi Guerrier. Enrichir la vie des gens, avec profondeur et savoir, jusqu'à ouvrir de nouvelles possibilités.`;

const CONTRACT_TITLE = '## Ce que tu rends';

/**
 * The shape asked for, written out rather than described, for the reason the
 * scribe's contract gives: a model given a template to fill answers in the
 * template, and the keys are those of `noyauAnalystOutputSchema`.
 *
 * The six triplets are listed from `NOYAUX`, in the order of the table, so the
 * template and the schema cannot disagree on a code.
 */
const CONTRACT = `Six notes, une par noyau, chacune avec sa justification :
- « score » : un entier de 0 à 100. 0, la matière contredit ce noyau ; 100, elle le soutient pleinement. Les notes sont indépendantes : elles ne se partagent pas cent points, et deux noyaux peuvent recevoir la même note.
- « justification » : une seule phrase, en français, qui dit ce qui dans la matière porte la note.

Tu ne classes pas les noyaux : tu les notes, et le programme classe.

Réponds UNIQUEMENT par un objet JSON, sans texte avant ni après, sans bloc de code, à ce format exact, avec chacun des six triplets une fois et une seule. Les 0 du gabarit sont des places à remplir, pas des réponses.

{
  "scores": [
${NOYAUX.map(
  (noyau) => `    { "noyau": "${noyau.code}", "score": 0, "justification": "<une phrase>" }`,
).join(',\n')}
  ]
}`;

/** The four parts of §4.9, in the order the system assembles them. */
export type AnalystSystemPartKind = 'framing' | 'grid' | 'reading' | 'contract';

/**
 * One part of the system block, as the panel « Ce que l'analyste recevra »
 * draws it: the markdown heading it opens with, and what follows. Only the
 * body of `reading` comes from the protocol; everything else is the code's.
 */
export type AnalystSystemPart = {
  readonly kind: AnalystSystemPartKind;
  /** The heading the part opens with - null for the framing, which has none. */
  readonly heading: string | null;
  /** What follows the heading: for `reading`, the protocol's reading, verbatim. */
  readonly body: string;
};

/** The four parts for one reading, in the order of §4.9. */
export function analystSystemParts(reading: string): readonly AnalystSystemPart[] {
  return [
    { kind: 'framing', heading: null, body: FRAMING },
    { kind: 'grid', heading: GRID_TITLE, body: GRID },
    { kind: 'reading', heading: ANALYST_READING_TITLE, body: reading },
    { kind: 'contract', heading: CONTRACT_TITLE, body: CONTRACT },
  ];
}

/** One part as the analyst receives it: its heading, a blank line, then its body. */
export function analystSystemPartText(part: AnalystSystemPart): string {
  return part.heading === null ? part.body : `${part.heading}\n\n${part.body}`;
}

/**
 * THE system block of the analyst for one reading: the four parts, separated
 * by one blank line (N32). The same for every session of one reading, which is
 * what lets a provider's cache point serve the sessions of one protocol (N43).
 */
export function analystSystemPrompt(reading: string): string {
  return analystSystemParts(reading).map(analystSystemPartText).join('\n\n');
}

/**
 * Does a protocol carry a reading? A reading made of spaces alone does not
 * (N35): the refusals and warnings of §3.7 compare it after a trim.
 */
export function hasAnalystReading(reading: string): boolean {
  return reading.trim() !== '';
}

/**
 * The reading a determination SERVES for the protocol a session froze (§4.9):
 * its own - or, when it has none, the generic one (N38).
 *
 * That second branch is reached by ONE kind of session only: one started
 * before the delivery of #154, whose frozen protocol was written before the
 * field existed. The refusal at start-up (§3.7) keeps every session of after
 * from getting there, and Sessions writes « Lecture : générique » for it.
 */
export function servedAnalystReading(reading: string): string {
  return hasAnalystReading(reading) ? reading : ANALYST_READING_TEMPLATE;
}

/**
 * What the analyst reads in place of an objective the reading cites and the
 * version no longer carries (noyau.md §4.9, V3, N51): a fixed label, in French
 * and in code like the framing, that the panel shows too. Never a setting.
 */
export const ANALYST_READING_ORPHAN_LABEL = '(objectif retiré)';

/**
 * The reading as the analyst RECEIVES it (noyau.md §4.9, issue #158, N50):
 * every `{{objectif:id}}` replaced by the name `steps` give that objective,
 * bare - quotes, when wanted, are typed around the chip -, and a token whose
 * objective they do not carry by `ANALYST_READING_ORPHAN_LABEL`.
 *
 * It never renders a matter (the condition that opened #158): the reading must
 * stay the same text for every session of one protocol version. Everything
 * else is left as typed, a `{{critere:...}}` included - only objectives are
 * cited in a reading (N48), and a criterion token there is text.
 *
 * `steps` are the frozen protocol's for the server, the draft's for the panel
 * « Ce que l'analyste recevra »: a draft objective whose name is still empty
 * renders empty, which no version can store (its name is required there). The
 * name is TRIMMED, as a version stores it, so the panel of a draft whose name
 * is still being typed shows what the analyst of the saved version will read.
 */
export function resolveAnalystReading(
  reading: string,
  steps: readonly ProtocolStepDraft[],
): string {
  const names = new Map<string, string>();
  for (const step of steps) {
    for (const objective of step.objectives) names.set(objective.id, objective.name.trim());
  }
  return renderVariables(reading, (variable) =>
    variable.kind === 'objective' ? (names.get(variable.id) ?? ANALYST_READING_ORPHAN_LABEL) : null,
  );
}
